package com.evoapps.evoshift.domain.model

/**
 * Hiring (SH-125): a manager's job post with a public page on evoshift.app (`job_posts/{id}`),
 * and the applications people send through it (`job_posts/{id}/applications/{appId}`, written
 * only by the server: functions/hiring.js). Applicants have no account; the workplace's owner is
 * the controller of their data, EVoShift the processor. The server deletes an application 90
 * days after the decision (12 months with the consent to future recruitment, 30 days after
 * hiring, at most 180 days while nobody decides).
 */
enum class JobPostStatus { DRAFT, OPEN, CLOSED }

/** The kind of contract offered (the page names it in the applicant's language). */
enum class JobContractType { EMPLOYMENT, MANDATE, B2B, OTHER }

/** What the pay range is per: an hour or a month (gross). */
enum class PayUnit { HOUR, MONTH }

/** The stages of an applicant in the pipeline. */
enum class ApplicantStatus {
    NEW, CONTACTED, TRIAL, HIRED, REJECTED;

    /** A decision was made (the retention runs from it). */
    val isDecided: Boolean get() = this == HIRED || this == REJECTED

    companion object {
        fun fromId(id: String?): ApplicantStatus = entries.firstOrNull { it.name == id } ?: NEW
    }
}

/** SH-210: whether a post's form asks for a CV. */
enum class CvRequirement { REQUIRED, OPTIONAL, NONE }

/** SH-210: the kinds of CV file a post may accept (functions/hiring.js checks each by content). */
enum class CvFileType(val id: String) {
    PDF("pdf"), DOC("doc"), DOCX("docx"), JPG("jpg"), PNG("png");

    companion object {
        fun fromId(id: String?): CvFileType? = entries.firstOrNull { it.id == id }
    }
}

/**
 * SH-210: the CV a post asks for: [requirement], the file kinds and the largest size in MB (small
 * files only: 1 to [MAX_MB]). Posts saved before have PDF and DOCX up to 5 MB, optional.
 */
data class CvSettings(
    val requirement: CvRequirement = CvRequirement.OPTIONAL,
    val types: Set<CvFileType> = setOf(CvFileType.PDF, CvFileType.DOCX),
    val maxMb: Int = MAX_MB
) {
    companion object {
        const val MIN_MB = 1
        const val MAX_MB = 5
    }
}

/**
 * SH-210: the post's rejection e-mail. [enabled]: the reject dialog offers it ticked. Blank
 * [subject] or [body]: the polite default in the applicant's language. The texts may say {name},
 * {job} and {employer}.
 */
data class RejectionMailTemplate(
    val enabled: Boolean = false,
    val subject: String = "",
    val body: String = ""
) {
    companion object {
        const val MAX_SUBJECT = 200
        const val MAX_BODY = 3000
        val PLACEHOLDERS = listOf("{name}", "{job}", "{employer}")
    }
}

data class JobPost(
    val id: String,
    val workplaceId: String,
    val status: JobPostStatus = JobPostStatus.DRAFT,
    /** Gender-neutral (art. 18³ᶜᵃ KP, the editor reminds). */
    val title: String = "",
    val role: String = "",
    val description: String = "",
    /** Free text: "mornings, 20–30 hours a week". */
    val hoursText: String = "",
    val contractType: JobContractType = JobContractType.EMPLOYMENT,
    /** The pay range, gross; required by the pay transparency rules (art. 18³ᶜᵃ KP). */
    val payMin: Double = 0.0,
    val payMax: Double = 0.0,
    val payUnit: PayUnit = PayUnit.HOUR,
    val currency: String = "PLN",
    val city: String = "",
    /** The last day applications are taken ("YYYY-MM-DD"); null: until closed. */
    val closesOn: String? = null,
    /** Who the applicants' data goes to (the controller), shown in the page's privacy notice. */
    val employerName: String = "",
    /** How applicants reach the employer about their data (e-mail or phone). */
    val privacyContact: String = "",
    /** The public page's language. */
    val lang: String = "pl",
    val createdAt: String = "",
    val createdBy: String? = null,
    val updatedAt: String = "",
    val updatedBy: String? = null,
    /** Kept by the server. */
    val totalApplicants: Int = 0,
    val newApplicants: Int = 0,
    val closedAt: String? = null,
    /** SH-210: the CV the form asks for. */
    val cv: CvSettings = CvSettings(),
    /**
     * SH-210: people of the team who read this post's applications and are told of new ones
     * (with the owners). Empty: every manager is told, as before.
     */
    val recruiterIds: List<String> = emptyList(),
    /** SH-210: the rejection e-mail. */
    val rejectionMail: RejectionMailTemplate = RejectionMailTemplate()
) {
    /** The public page to share. */
    val shareUrl: String get() = shareUrlOf(id)

    companion object {
        const val MAX_TITLE = 120
        const val MAX_ROLE = 50
        const val MAX_DESCRIPTION = 2000
        const val MAX_HOURS = 200
        const val MAX_CITY = 100
        const val MAX_EMPLOYER = 200
        const val MAX_CONTACT = 200
        const val MAX_PAY = 1_000_000.0
        const val MAX_RECRUITERS = 20

        fun shareUrlOf(postId: String) = "https://evoshift.app/jobs/$postId"
    }
}

/** The CV an applicant attached (kept privately; opened through a short-lived link). */
data class ApplicantCv(val fileName: String, val contentType: String, val sizeBytes: Long)

data class JobApplication(
    val id: String,
    val postId: String,
    val workplaceId: String,
    val name: String,
    val email: String? = null,
    val phone: String? = null,
    val availability: String? = null,
    val note: String? = null,
    /** Consented to future recruitment (kept 12 months instead of 90 days). */
    val futureRecruitment: Boolean = false,
    val cv: ApplicantCv? = null,
    val status: ApplicantStatus = ApplicantStatus.NEW,
    val managerNote: String? = null,
    val decidedAt: String? = null,
    val createdAt: String = "",
    /** When the server deletes it (ISO-8601), when known. */
    val deletesAt: String? = null,
    /** SH-210: the language the applicant applied in (their e-mails are in it). */
    val lang: String = "pl",
    /** SH-210: the readers who opened it (each reader's own read state). */
    val readBy: Set<String> = emptySet(),
    /** SH-210: the readers who starred it. */
    val starredBy: Set<String> = emptySet(),
    /** SH-210: when the rejection e-mail was queued (the server's; once per application). */
    val rejectionMailAt: String? = null
) {
    fun isReadBy(uid: String): Boolean = uid in readBy
    fun isStarredBy(uid: String): Boolean = uid in starredBy

    companion object {
        const val MAX_MANAGER_NOTE = 1000
    }
}

/** SH-210: the list's filter of a post's applications, for the viewer. */
enum class ApplicationFilter {
    ALL, UNREAD, STARRED, REJECTED;

    /** Whether [application] shows under this filter for [viewerId]. */
    fun matches(application: JobApplication, viewerId: String): Boolean = when (this) {
        ALL -> true
        UNREAD -> !application.isReadBy(viewerId)
        STARRED -> application.isStarredBy(viewerId)
        REJECTED -> application.status == ApplicantStatus.REJECTED
    }
}

/** A link to an applicant's CV, valid for a few minutes. */
data class CvLink(val url: String, val fileName: String, val expiresAt: String)

/** Why a job post cannot be saved; the editor names the field. */
class InvalidJobPostException(val problem: Problem) : Exception("Invalid job post: $problem") {
    enum class Problem {
        TITLE_REQUIRED, PAY_REQUIRED, PAY_RANGE, EMPLOYER_REQUIRED, CONTACT_REQUIRED, DATE_INVALID, TOO_LONG,
        /** SH-210: a CV is asked for, but no file kind is allowed. */
        CV_TYPES_REQUIRED
    }
}

/**
 * SH-210: whether the person [userId] with [capabilities] at the post's workplace reads its
 * applications: the managers (hiring rights, [Capability.MANAGE_TEAM]) and the post's recruiters
 * (firestore.rules readsApplicationsOf).
 */
fun JobPost.isReaderFor(userId: String, capabilities: Set<Capability>): Boolean =
    Capability.MANAGE_TEAM in capabilities || (userId in recruiterIds && Capability.WORK_SHIFTS in capabilities)

/** SH-210: whether that reader may act on them now (the stage, rejecting, interviews: the plan runs). */
fun JobPost.isActorFor(userId: String, capabilities: Set<Capability>): Boolean =
    isReaderFor(userId, capabilities) && Capability.MAKE_CHANGES in capabilities
